import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { User, Settings } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { useAuth } from '@/hooks/useAuth'
import { apiClient, getApiErrorMessage } from '@/lib/api-client'
import { formatDate } from '@shared/utils'
import toast from 'react-hot-toast'

interface ProfileForm {
  name: string
  email: string
}

interface PasswordForm {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export function ProfilePage() {
  const { user, updateUser } = useAuth()
  const [profileOpen, setProfileOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [savingProfile, setSavingProfile] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)

  const profileForm = useForm<ProfileForm>()
  const passwordForm = useForm<PasswordForm>()

  if (!user) {
    return null
  }

  const openProfile = () => {
    profileForm.reset({ name: user.name, email: user.email })
    setProfileOpen(true)
  }

  const openPassword = () => {
    passwordForm.reset({ currentPassword: '', newPassword: '', confirmPassword: '' })
    setPasswordOpen(true)
  }

  const saveProfile = async (data: ProfileForm) => {
    setSavingProfile(true)
    try {
      const response = await apiClient.put<{ user: typeof user }>('/auth/profile', {
        name: data.name.trim(),
        email: data.email.trim(),
      })
      updateUser(response.data.user)
      toast.success('Profile updated')
      setProfileOpen(false)
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not update profile'))
    } finally {
      setSavingProfile(false)
    }
  }

  const savePassword = async (data: PasswordForm) => {
    setSavingPassword(true)
    try {
      await apiClient.put('/auth/password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      })
      toast.success('Password updated')
      setPasswordOpen(false)
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not change password'))
    } finally {
      setSavingPassword(false)
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold tracking-tight">Profile</h1>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{user.name}</h3>
                  <p className="text-muted-foreground">{user.email}</p>
                  <Badge variant={user.role === 'Admin' ? 'destructive' : 'default'} className="mt-1">
                    {user.role}
                  </Badge>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Full Name</label>
                  <p className="text-sm">{user.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Email Address</label>
                  <p className="text-sm">{user.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Employee ID</label>
                  <p className="text-sm">{user.employeeId || '—'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Role</label>
                  <p className="text-sm">{user.role}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Member Since</label>
                  <p className="text-sm">{formatDate(user.createdAt)}</p>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Button type="button" onClick={openProfile}>
                  <Settings className="mr-2 h-4 w-4" />
                  Edit Profile
                </Button>
                <Button type="button" variant="outline" onClick={openPassword}>
                  Change Password
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Account Status</span>
                <Badge variant="success">Active</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Last Login</span>
                <span className="text-sm">Just now</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Profile Complete</span>
                <span className="text-sm">100%</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Modal isOpen={profileOpen} onClose={() => setProfileOpen(false)} title="Edit profile" size="sm">
        <form onSubmit={profileForm.handleSubmit(saveProfile)} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium required">Full name</label>
            <Input
              {...profileForm.register('name', {
                required: 'Name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' },
              })}
              className={profileForm.formState.errors.name ? 'border-destructive' : ''}
            />
            {profileForm.formState.errors.name && (
              <p className="mt-1 text-sm text-destructive">{profileForm.formState.errors.name.message}</p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium required">Email</label>
            <Input
              type="email"
              {...profileForm.register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Enter a valid email address',
                },
              })}
              className={profileForm.formState.errors.email ? 'border-destructive' : ''}
            />
            {profileForm.formState.errors.email && (
              <p className="mt-1 text-sm text-destructive">{profileForm.formState.errors.email.message}</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setProfileOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={savingProfile}>
              {savingProfile ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={passwordOpen} onClose={() => setPasswordOpen(false)} title="Change password" size="sm">
        <form onSubmit={passwordForm.handleSubmit(savePassword)} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium required">Current password</label>
            <Input
              type="password"
              autoComplete="current-password"
              {...passwordForm.register('currentPassword', { required: 'Current password is required' })}
              className={passwordForm.formState.errors.currentPassword ? 'border-destructive' : ''}
            />
            {passwordForm.formState.errors.currentPassword && (
              <p className="mt-1 text-sm text-destructive">{passwordForm.formState.errors.currentPassword.message}</p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium required">New password</label>
            <Input
              type="password"
              autoComplete="new-password"
              {...passwordForm.register('newPassword', {
                required: 'New password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
              className={passwordForm.formState.errors.newPassword ? 'border-destructive' : ''}
            />
            {passwordForm.formState.errors.newPassword && (
              <p className="mt-1 text-sm text-destructive">{passwordForm.formState.errors.newPassword.message}</p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium required">Confirm new password</label>
            <Input
              type="password"
              autoComplete="new-password"
              {...passwordForm.register('confirmPassword', {
                required: 'Confirm your new password',
                validate: (value) => value === passwordForm.getValues('newPassword') || 'Passwords do not match',
              })}
              className={passwordForm.formState.errors.confirmPassword ? 'border-destructive' : ''}
            />
            {passwordForm.formState.errors.confirmPassword && (
              <p className="mt-1 text-sm text-destructive">{passwordForm.formState.errors.confirmPassword.message}</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setPasswordOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={savingPassword}>
              {savingPassword ? 'Saving...' : 'Update password'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
